import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { API_BASE } from "@/lib/apiBase";

export const api = axios.create({ baseURL: API_BASE, withCredentials: true });

// The access cookie lasts 15 minutes. When any call comes back 401, renew the session once
// (shared between simultaneous calls) and retry, so long practices and reflections never lose their work.
let renewing = null;
api.interceptors.response.use(undefined, async (error) => {
  const cfg = error.config;
  const url = cfg?.url ?? "";
  if (error.response?.status !== 401 || !cfg || cfg._retried || url.startsWith("/auth/")) throw error;
  cfg._retried = true;
  renewing = renewing ?? api.post("/auth/refresh").finally(() => { renewing = null; });
  await renewing;
  return api(cfg);
});

export const errText = (d, fallback = "Something went wrong. Please try again.") => {
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg ?? "").filter(Boolean).join(" ") || fallback;
  return d?.msg ?? fallback;
};

export const withRefresh = async (fn) => {
  try {
    return await fn();
  } catch (e) {
    if (e.response?.status !== 401) throw e;
    await api.post("/auth/refresh");
    return fn();
  }
};

const AuthCtx = createContext({ user: null, refresh: async () => {}, logout: async () => {}, setUser: () => {} });

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const refresh = useCallback(async () => {
    if (!process.env.REACT_APP_BACKEND_URL) {
      setUser(false);
      return false;
    }
    try {
      const { data } = await withRefresh(() => api.get("/auth/me"));
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    }
  }, []);
  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(false);
    }
  }, []);
  useEffect(() => {
    refresh();
  }, [refresh]);
  return <AuthCtx.Provider value={{ user, setUser, refresh, logout }}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => useContext(AuthCtx);
